import React from "react";

/**
 * Visual companion for the About page — a compact "operator cycle"
 * showing MTS's four operational pillars as an interconnected loop.
 * Purely decorative, monochrome, uses the site's line grammar.
 *
 * No invented statistics. The labels below correspond to approved values
 * and process bullets from the Brand & Copy Bible.
 */
export default function AboutCycle({ className = "" }) {
  const nodes = [
    { id: "spec",    label: "Specify",  hint: "Written brief", angle: -90 },
    { id: "verify",  label: "Verify",   hint: "On-site checks", angle: 0 },
    { id: "execute", label: "Execute",  hint: "Production & QC", angle: 90 },
    { id: "deliver", label: "Deliver",  hint: "Docs & handover", angle: 180 },
  ];

  const cx = 200;
  const cy = 200;
  const r = 130;

  const pos = (angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  return (
    <svg
      viewBox="0 0 400 400"
      className={className}
      role="img"
      aria-label="MTS operator cycle: specify, verify, execute, deliver"
    >
      <defs>
        <radialGradient id="acHaze" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#0a0a0a" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0" />
        </radialGradient>
        <pattern id="acDots" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="#0a0a0a" fillOpacity="0.08" />
        </pattern>
        <radialGradient id="acFadeGrad" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <mask id="acFade">
          <rect width="400" height="400" fill="url(#acFadeGrad)" />
        </mask>
      </defs>

      <g mask="url(#acFade)">
        <rect width="400" height="400" fill="url(#acDots)" />
      </g>
      <circle cx={cx} cy={cy} r={r + 40} fill="url(#acHaze)" />

      {/* outer dashed ring */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#0a0a0a"
        strokeOpacity="0.35"
        strokeDasharray="2 6"
      />
      {/* subtle inner ring */}
      <circle cx={cx} cy={cy} r={r - 40} fill="none" stroke="#0a0a0a" strokeOpacity="0.08" />

      {/* connection arrows between nodes (subtle) */}
      {nodes.map((n, i) => {
        const a = n.angle;
        const b = nodes[(i + 1) % nodes.length].angle;
        // arc from a to b along the ring
        const start = pos(a + 10);
        const end = pos(b - 10);
        return (
          <path
            key={n.id + "-arc"}
            d={`M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`}
            stroke="#0a0a0a"
            strokeOpacity="0.35"
            strokeWidth="1"
            fill="none"
          />
        );
      })}

      {/* central hub */}
      <circle cx={cx} cy={cy} r="36" fill="#ffffff" stroke="#e5e5ea" />
      <circle cx={cx} cy={cy} r="26" fill="#0a0a0a" />
      <text
        x={cx}
        y={cy + 4}
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="11"
        fill="#ffffff"
        fontWeight="600"
        letterSpacing="1.4"
      >
        MTS
      </text>

      {/* four nodes */}
      {nodes.map((n) => {
        const { x, y } = pos(n.angle);
        // Text offset: place label outside the ring
        const rad = (n.angle * Math.PI) / 180;
        const lx = cx + (r + 34) * Math.cos(rad);
        const ly = cy + (r + 34) * Math.sin(rad);
        return (
          <g key={n.id}>
            {/* connection tick from hub to node */}
            <line
              x1={cx + 40 * Math.cos(rad)}
              y1={cy + 40 * Math.sin(rad)}
              x2={x - 12 * Math.cos(rad)}
              y2={y - 12 * Math.sin(rad)}
              stroke="#0a0a0a"
              strokeOpacity="0.15"
            />
            <circle cx={x} cy={y} r="14" fill="#ffffff" stroke="#0a0a0a" strokeOpacity="0.2" />
            <circle cx={x} cy={y} r="4" fill="#0a0a0a" />
            <text
              x={lx}
              y={ly}
              textAnchor="middle"
              fontFamily="Inter, sans-serif"
              fontSize="12"
              fontWeight="600"
              fill="#0a0a0a"
            >
              {n.label}
            </text>
            <text
              x={lx}
              y={ly + 14}
              textAnchor="middle"
              fontFamily="Inter, sans-serif"
              fontSize="10"
              fill="#6e6e73"
            >
              {n.hint}
            </text>
          </g>
        );
      })}

      {/* technical brackets */}
      {[
        [20, 20, 40, 20, 20, 40],
        [380, 20, 360, 20, 380, 40],
        [20, 380, 40, 380, 20, 360],
        [380, 380, 360, 380, 380, 360],
      ].map(([a, b, c, d, e, f], i) => (
        <g key={i} stroke="#0a0a0a" strokeOpacity="0.3">
          <line x1={a} y1={b} x2={c} y2={d} />
          <line x1={a} y1={b} x2={e} y2={f} />
        </g>
      ))}

      <text
        x="20"
        y="14"
        fontFamily="Inter, sans-serif"
        fontSize="9"
        fill="#86868b"
        letterSpacing="2"
      >
        MTS / OPERATOR CYCLE
      </text>
    </svg>
  );
}
